import { Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import { Button } from '../../components/button'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { TextInput } from '../../components/inputs/textInput'
import ModalComponent from '../../components/modal'
import { ConfirmationModal } from '../../components/modal/app_modals/confirmationModal'
import { TableComponent } from '../../components/table'
import { HeaderSubheader } from '../../components/typography/HeaderSubheader'
import { getPageFromSkip, getSkipFromPage } from '../../helpers'
import {
  useCreateInvestorMutation,
  useGetAllInvestorsQuery,
  useInviteInvestorMutation,
  useUpdateInvestorMutation,
} from '../../redux-toolkits/investor/investor.slice'
import { Investor } from '../../redux-toolkits/investor/investor.type'
import {
  useFetchBanksQuery,
  useResolveBankDetailsQuery,
} from '../../redux-toolkits/misc/bank.slice'
import { QueryProps } from '../../redux-toolkits/shared/shared.type'
import { SettlementAccount } from '../../redux-toolkits/settlements/settlements.type'
const GoodInvestors: React.FC = () => {
  const [query, setQuery] = useState<QueryProps>({
    page: 1,
    limit: 20,
    search: undefined,
  })
  const [editing, setEditing] = useState<Investor | undefined>()
  const [deleting, setDeleting] = useState<Investor | undefined>()
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const navigate = useNavigate()

  const [payload, setPayload] = useState<SettlementAccount>({
    accountNumber: editing?.settlementAccount?.accountNumber ?? '',
    accountName: editing?.settlementAccount?.accountName ?? '',
    bankName: editing?.settlementAccount?.bankName ?? '',
    bankCode: editing?.settlementAccount?.bankCode ?? '',
  })

  const { data, isFetching, refetch } = useGetAllInvestorsQuery({ ...query })

  const [inviteInvestor, { isLoading: isSendLink }] =
    useInviteInvestorMutation()

  const closeModal = (): void => {
    setOpenModal(false)
    setEditing(undefined)
  }

  return (
    <div className="flex flex-col gap-10">
      <HeaderSubheader
        title="Good Stakeholders"
        text="A list of all the stakeholders including their name,
              title, email and role."
      />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="!w-full md:!max-w-[400px]">
          <SearchInput
            searchFunction={(search) => {
              setQuery({
                ...query,
                search: search === '' ? undefined : search,
                page: 1,
              })
            }}
            placeholder="Search Good Stakeholders"
          />
        </div>

        <Button
          type="button"
          label="Create Stakeholder"
          primary
          size="medium"
          onClick={() => {
            setOpenModal(true)
          }}
        />
      </div>

      <div className="w-full overflow-x-scroll">
        <TableComponent
          loading={isFetching}
          headers={[
            'Full name',
            'Email',
            'Account Name',
            'Account Number',
            'Bank Name',
            'Onboarded',
            'Status',
            'Actions',
          ]}
          rows={
            data?.data?.docs.map((stakeholder) => {
              return {
                id: stakeholder._id,
                content: [
                  stakeholder.fullName,
                  stakeholder.email,
                  stakeholder.settlementAccount.accountName,
                  stakeholder.settlementAccount.accountNumber,
                  stakeholder.settlementAccount.bankName,
                  stakeholder.isAccountSetup ? 'Yes' : 'No',
                  <Status
                    key={`${stakeholder._id}-status`}
                    text={stakeholder.isActive ? 'Active' : 'Inactive'}
                    type={stakeholder.isActive ? 'success' : 'fail'}
                  />,
                  <Dropdown
                    className="text-left"
                    key={`${stakeholder._id}-controls`}
                    menuButton={
                      <Icon id="ellipses" height={18} width={18} className="" />
                    }
                    menuItems={[
                      {
                        name: 'Edit',
                        value: '',
                        onClick: () => {
                          setEditing(stakeholder)
                          setPayload({
                            accountNumber:
                              stakeholder?.settlementAccount?.accountNumber,
                            accountName:
                              stakeholder?.settlementAccount?.accountName,
                            bankName: stakeholder?.settlementAccount?.bankName,
                            bankCode: stakeholder?.settlementAccount?.bankCode,
                          })
                          setOpenModal(true)
                        },
                      },
                      {
                        name: stakeholder.isActive ? 'Deactivate' : 'Activate',
                        value: '',
                        onClick: () => {
                          setDeleting(stakeholder)
                          setOpenDeleteModal(true)
                        },
                      },
                      {
                        name: stakeholder.isAccountSetup
                          ? 'Onboarded'
                          : 'Send onboarding link',
                        value: '',
                        onClick: async () => {
                          if (!stakeholder.isAccountSetup) {
                            await inviteInvestor(stakeholder._id)
                          }
                        },
                      },
                      {
                        name: 'Detail',
                        value: '',
                        onClick: async () => {
                          navigate(`/stakeholder/${stakeholder._id}`)
                        },
                      },
                    ]}
                  />,
                ],
              }
            }) ?? []
          }
          isEmpty={[0, undefined].includes(data?.data?.docs.length)}
          currentPage={getPageFromSkip(Number(query.page), Number(query.limit))}
          totalDataCount={data?.data.totalPages ?? 0}
          pageLimit={Number(query.limit)}
          onPageChange={(number) => {
            setQuery({
              ...query,
              page: getSkipFromPage(number, Number(query.limit)),
            })
          }}
        />
      </div>

      <CreateInvestorModal
        stakeholder={editing}
        open={openModal}
        payload={payload}
        setPayload={setPayload}
        closeModal={() => {
          closeModal()
          refetch()
        }}
      />

      <ActivateInvestorMemberModal
        stakeholder={deleting}
        open={openDeleteModal}
        closeModal={() => {
          setOpenDeleteModal(false)
          setDeleting(undefined)
          refetch()
        }}
      />
    </div>
  )
}

const ActivateInvestorMemberModal: React.FC<{
  open: boolean
  closeModal: () => void
  stakeholder?: Investor
}> = ({ open, closeModal, stakeholder }) => {
  const [action, { isLoading }] = useUpdateInvestorMutation()

  return (
    <>
      <ConfirmationModal
        loading={isLoading}
        open={open}
        setOpen={() => {}}
        onCloseCallback={() => {
          closeModal()
        }}
        title={`${
          stakeholder?.isActive ? 'Deactivate' : 'Activate'
        } stakeholder`}
        message={`Are you sure you want to ${
          stakeholder?.isActive ? 'deactivate' : 'activate'
        } ${stakeholder?.fullName}?`}
        onConfirm={async () => {
          if (stakeholder) {
            const updateData = {
              fullName: stakeholder.fullName,
              isActive: stakeholder?.isActive ? false : true,
              settlementAccount: {
                accountNumber: stakeholder.settlementAccount.accountName,
                accountName: stakeholder.settlementAccount.accountName,
                bankName: stakeholder.settlementAccount.bankName,
                bankCode: stakeholder.settlementAccount.bankCode,
              },
            }

            await action({
              body: updateData,
              id: stakeholder._id,
            })

            closeModal()
          }
        }}
        confirmText={stakeholder?.isActive ? 'Deactivate' : 'Activate'}
      />
    </>
  )
}

const CreateInvestorModal: React.FC<{
  open: boolean
  closeModal: () => void
  stakeholder?: Investor
  setPayload: React.Dispatch<React.SetStateAction<SettlementAccount>>
  payload: SettlementAccount
}> = ({ open, closeModal, stakeholder, payload, setPayload }) => {
  const [createInvestor, { isLoading: isCreateInvestor }] =
    useCreateInvestorMutation()
  const [updateInvestor, { isLoading: isUpdatingInvestor }] =
    useUpdateInvestorMutation()

  const { data: banks, isFetching: fetchingBanks } =
    useFetchBanksQuery(undefined)

  const {
    data: accountDetails,
    isFetching: resolvingAccount,
    refetch: resolveAccount,
    isError: resolveAccountError,
    isSuccess: resolveAccountSuccess,
  } = useResolveBankDetailsQuery(
    {
      accountNumber: payload?.accountNumber,
      bankCode: payload?.bankCode,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !payload?.accountNumber || !payload?.bankCode,
    },
  )

  useEffect(() => {
    if (accountDetails?.data) {
      setPayload({
        ...payload,
        accountName: accountDetails.data.account_name,
      })
    }
  }, [accountDetails])

  return (
    <>
      <ModalComponent
        title={`${
          stakeholder !== undefined ? 'Edit' : 'Create new'
        } stakeholder`}
        open={open}
        setOpen={() => {}}
        onCloseCallback={() => {
          closeModal()
        }}
        childrenClassName="md:!max-w-[420px]"
      >
        <Formik
          enableReinitialize={true}
          initialValues={{
            fullName: stakeholder?.fullName ?? '',
            email: stakeholder?.email ?? '',
          }}
          validationSchema={Yup.object({
            fullName: Yup.string().required('Full name required'),
            email: Yup.string()
              .email('Invalid email address')
              .required('Required'),
          })}
          onSubmit={async (values) => {
            const data = {
              ...values,
              settlementAccount: {
                accountNumber: payload.accountNumber,
                accountName: payload.accountName,
                bankName: payload.bankName,
                bankCode: payload.bankCode,
              },
            }

            if (stakeholder !== null && stakeholder !== undefined) {
              const updateData = {
                fullName: values.fullName,
                settlementAccount: {
                  accountNumber: payload.accountNumber,
                  accountName: payload.accountName,
                  bankName: payload.bankName,
                  bankCode: payload.bankCode,
                },
              }

              await updateInvestor({
                body: updateData,
                id: stakeholder._id,
              })

              closeModal()
            } else {
              await createInvestor(data)
              closeModal()
              setPayload({
                accountNumber: '',
                accountName: '',
                bankName: '',
                bankCode: '',
              })
            }
          }}
        >
          {({
            values,
            handleBlur,
            handleChange,
            errors,
            touched,
            setFieldValue,
            setValues,
          }) => (
            <Form className="flex flex-col gap-4">
              <TextInput
                type="text"
                name="fullName"
                placeholder="Full name"
                value={values.fullName}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={
                  errors.fullName && touched.fullName ? errors.fullName : ''
                }
                hasIcon={false}
              />

              <TextInput
                type="text"
                name="email"
                placeholder="Email"
                value={values.email}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={errors.email && touched.email ? errors.email : ''}
                hasIcon={false}
                disabled={!!stakeholder?._id}
              />

              <SearchInput
                hasIcon={false}
                useDefaultTextAppearance
                placeholder="Account Number"
                searchFunction={(search) => {
                  setPayload({
                    ...payload,
                    accountNumber: search,
                  })
                }}
                initialValue={payload.accountNumber}
                className="max-w-[100%]"
              />

              <Dropdown
                loading={fetchingBanks}
                menuClassName="!max-h-[300px]"
                menuButton={
                  <TextInput
                    hasIcon={false}
                    value={payload.bankName}
                    readOnly
                    placeholder="Choose Bank"
                    className="!min-w-full"
                  />
                }
                menuItems={banks?.data.map((bank) => {
                  return {
                    name: bank.name,
                    value: bank.code,
                    onClick: () => {
                      setPayload({
                        ...payload,
                        bankName: bank.name as string,
                        bankCode: bank.code as string,
                      })
                    },
                  }
                })}
                className="w-full"
              />

              <p
                className={`${
                  resolveAccountError ? 'text-danger' : 'text-sec-black'
                }`}
              >
                {resolvingAccount && 'Resolving bank details...'}
                {!resolvingAccount && resolveAccountError
                  ? 'Could not resolve bank details...'
                  : undefined}
                {!resolvingAccount && !resolveAccountError
                  ? payload?.accountName
                  : undefined}
              </p>

              <Button
                disabled={isCreateInvestor || isUpdatingInvestor}
                loading={isCreateInvestor || isUpdatingInvestor}
                type="submit"
                label="Proceed"
                primary
                className="!mt-6 !w-full"
              />
            </Form>
          )}
        </Formik>
      </ModalComponent>
    </>
  )
}

export default GoodInvestors
